import { useState } from 'react'

import { useVorschlaegeLoeschen, useVorschlaegeZahl } from '../../hooks/queries'
import { useOnline } from '../../hooks/useOutbox'
import { rueckfrage } from '../../components/Rueckfrage'
import { fehlertext } from '../../api/client'
import { useTexte } from '../../texte'

/**
 * Delete the suggestions from the route, all at once (#100, 1.54.0).
 *
 * From use: "eine weitere Option bei Zurücksetzen: Vorschläge (POIs)
 * löschen -- zur Bereinigung." An import lays a point on every stop; on a
 * trip that comes and goes the same way that is soon a map full of
 * "Aufenthalt, 2 Std.", and taking them away one by one is a chore.
 *
 * **Smaller than the reset, on purpose.** The reset asks for a typed code
 * because nothing comes back; a suggestion is what the import made and
 * nobody wrote, and the next import makes it again. So: the number, one
 * question (`rueckfrage`), done. Drafts and published points stay -- the
 * server decides what a suggestion is (`origin = 'auto'`, never published),
 * the card only shows the count it was given.
 */
export function Vorschlaegeloeschen({ tripId }: { tripId: number }) {
  const texte = useTexte()
  const t = texte.reise.vorschlaege
  const zahl = useVorschlaegeZahl(tripId, true)
  const loeschen = useVorschlaegeLoeschen(tripId)
  const online = useOnline()
  const [befund, setBefund] = useState<string | null>(null)
  const anzahl = zahl.data?.highlights ?? 0

  return (
    <div className="card card--seite" data-vorschlaege={zahl.data === undefined ? '' : anzahl}>
      <h2 className="card__titel">{t.titel}</h2>
      <p className="feld__hinweis klein">{t.was}</p>
      <p className="zwischenzeile">
        {zahl.isPending ? t.wirdGezaehlt : zahl.isError ? fehlertext(zahl.error) : t.zahl(anzahl)}
      </p>
      {loeschen.isError && (
        <p className="notice" role="alert">{fehlertext(loeschen.error)}</p>
      )}
      <button
        type="button"
        className="button button--outline button--gefahr"
        disabled={anzahl === 0 || !online || loeschen.isPending}
        onClick={async () => {
          if (!(await rueckfrage(t.frage(anzahl), t.knopf))) return
          setBefund(null)
          const weg = await loeschen.mutateAsync()
          setBefund(t.fertig(weg.highlights))
        }}
      >
        {loeschen.isPending ? t.laeuft : t.knopf}
      </button>
      {befund !== null && (
        <p className="feld__hinweis" role="status" data-vorschlaege-geloescht="">{befund}</p>
      )}
    </div>
  )
}
